'use client';

import {z} from 'zod';
import {css} from '@panda/css';
import {WithClickOpen} from '@/hocs';
import {Box, Grid, GridItem} from '@panda/jsx';
import {Asset} from '@/components/atoms/asset';
import {GIFT_CARD_CODE_LENGTH} from '@/constants';
import {zodResolver} from '@hookform/resolvers/zod';
import {useRedeemStepsContext} from '@/context/RedeemStepsContext';
import {Container, ContentBlock, FormTextField, SafeHTML} from '@/components';
import {KEYBOARD_SUBMIT_KEYS} from '@/lib/utils/a11y';
import {useRouter} from 'next/navigation';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {getStaticAssetPath} from '@/lib/utils/assets';
import {getGiftCardRedirectUrl, hasExceededLimit, isCodeVerified} from '@/lib/utils/giftCard.utils';
import {isSameOrigin} from '@/lib/utils/page.utils';
import {genRoute, safelyReplaceBrowserUrl} from '@/lib/utils/route.utils';
import {Text, Button, QuestionCircleIcon, Stack, Tooltip} from '@eds-web/react';
import {mapClientError} from '@/lib/utils/error.utils';
import {FormEvent, useEffect, useRef, useState} from 'react';
import {RedirectModalFactory} from '../modal';
import {onEmitClickEvent} from '@/lib/utils/analytics';
import {useLocale, useMessages} from '@epic-mw/i18n';
import {SectionSideBySideFieldsFragment} from '@common/hygraph-schema';
import {
	useAccessCheck,
	useAutoRedeem,
	useGiftCardVerify,
	useIsLoggedIn,
	useMediaQuery,
	useQuickLinks
} from '@/hooks';
import {useSiteSettingsContext} from '@/context/SiteSettingsContext';

const emitVerifyCodeEvent = (
	event: FormEvent<HTMLFormElement> | null,
	eventValue?: Record<string, string>
) => {
	onEmitClickEvent({
		eventCategory: 'button',
		interactionType: 'click',
		eventAction: 'verifyCode',
		eventLabel: 'Verify Code Redeemable',
		eventValue: {
			component: 'VerifyStep',
			section: 'redeem',
			subView: 'Enter Code',
			interactedElement: 'Button_Redeem',
			...eventValue
		}
	})(event);
};

const buttonStyles = css({
	minWidth: '200px',
	width: {
		mdDown: '100%'
	}
});

const tooltipStyles = css({
	padding: '16px !important',
	maxWidth: '257px',
	'& p': {
		maxWidth: '225px'
	},
	'& img': {
		boxShadow: '10px 10px 10px rgba(0, 0, 0, 0.12)',
		borderRadius: '8px'
	}
});

// Ensure alphanumeric characters for each character.
const codeMask = Array(GIFT_CARD_CODE_LENGTH).fill(/[a-zA-Z0-9]/);

const schema = z.object({
	code: z
		.string()
		.length(GIFT_CARD_CODE_LENGTH, 'errors.com.epicgames.validation.min_length')
		.regex(/^[a-zA-Z0-9]+$/, 'epic.app.error.redeem.invalid.code')
});

type RedeemFormValues = z.infer<typeof schema>;

interface Props extends SectionSideBySideFieldsFragment {
	className?: string;
}

export const SideBySideVerifySection = (props: Props) => {
	const [mounted, setMounted] = useState(false);
	const locale = useLocale();
	const router = useRouter();
	const messages = useMessages();
	const quickLinks = useQuickLinks('faqAccountBalance');
	const {code, setNormalizedCode} = useRedeemStepsContext();
	const {cmsRedeemDisabled} = useSiteSettingsContext();
	const isTablet = useMediaQuery('md');
	const isMobile = useMediaQuery('sm');
	const isLoggedIn = useIsLoggedIn();
	const accessCheck = useAccessCheck();
	const methods = useForm<RedeemFormValues>({
		mode: 'onChange',
		resolver: zodResolver(schema as any),
		defaultValues: {code: code || ''}
	});

	const quickLinksRedeem = useQuickLinks('vbucksRedeem', {code});
	const codeInput = methods.watch('code');
	const {isSubmitting, isSubmitSuccessful} = methods.formState;
	const submitSource = useRef<'mouse' | 'keyboard' | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const {
		trigger,
		error,
		data,
		isMutating: isVerifying,
		reset
	} = useGiftCardVerify({
		onSuccess: (result) => {
			if (!result || 'error' in result) return; // false positives

			emitVerifyCodeEvent(null, {status: 'success', codeVerified: 'true'});

			if (hasExceededLimit(result)) {
				emitVerifyCodeEvent(null, {
					status: 'error',
					error: 'epic.app.error.addFunds.maxAmount'
				});
			}

			// onSuccess here still runs on successful code verification,
			// but the verifyAuthenticated redirect gets overridden if we don't specify `account &&`.
			if (isLoggedIn) {
				// TODO: Refactor this into genroutes.
				const current = new URLSearchParams(window.location.search);
				const preserved = new URLSearchParams();
				const source = current.get('source');
				const redirectUrl = current.get('redirectUrl');
				if (source) preserved.set('source', source);
				if (redirectUrl && isSameOrigin(redirectUrl)) {
					preserved.set('redirectUrl', redirectUrl);
				}
				const search = preserved.size ? `?${preserved.toString()}` : '';

				router.push(genRoute(`/${codeInput}/add-funds${search}`));
				router.refresh(); // refresh the page to get the new/default locale
			}
		},
		onError: (error) => {
			emitVerifyCodeEvent(null, {status: 'error', error});
		}
	});

	const codeVerified = data && isCodeVerified(data) && !error?.error;
	const completedStep = isSubmitSuccessful && codeVerified;
	const disableSubmit = !mounted || !!error?.error || cmsRedeemDisabled;

	// codeInput is our source of truth for the latest and currently inputted code - rare edge case can cause a discrepancy between data.code versus user's inputted code.
	const onValidSubmit: SubmitHandler<{code: string}> = async () => {
		const codeInputUppercase = codeInput.toUpperCase();

		safelyReplaceBrowserUrl(codeInputUppercase);
		setNormalizedCode(codeInputUppercase); // Ensure provider value for code reflects URL.

		const accessCheckResults = accessCheck.check({
			redirectIfFailUrl: getGiftCardRedirectUrl(codeInputUppercase),
			//eslint-disable-next-line @typescript-eslint/no-misused-promises
			action: (result) => {
				return trigger({newCode: codeInputUppercase, ...result}).catch(() => {}); // silence insignificant error from react-hook-form
			}
		});

		return accessCheckResults;
	};

	const handleAutoVerify = () => {
		emitVerifyCodeEvent(null, {status: 'initiated', autoRedeem: 'true'});
		return methods.handleSubmit(onValidSubmit)();
	};

	const autoRedeeming = useAutoRedeem(handleAutoVerify);
	const isLoading = isSubmitting || isVerifying || autoRedeeming || accessCheck.isLoading;
	const errorMessage =
		error?.error && mapClientError(messages, error.error, {...quickLinks, locale});

	useEffect(() => {
		setMounted(true);
	}, []);

	// Update form values when code changes (from context after first render).
	useEffect(() => {
		methods.reset({code});
		// router.replace can make user lose focus
		if (code) {
			inputRef.current?.focus();
		}
	}, [code]);

	return (
		<>
			<RedirectModalFactory
				onCloseAction={reset}
				pinType={error?.data?.pinType}
				redirectTo={quickLinksRedeem.vbucksRedeem}
			/>

			<Container
				boxClassName={css({overflow: 'hidden'})}
				display="grid"
				placeItems="center"
				bg={isMobile ? 'transparent' : undefined}>
				<Grid
					w="100%"
					gridTemplateColumns="1fr auto"
					alignItems="center"
					smDown={{
						gridTemplateColumns: '1fr',
						gap: 0,
						placeItems: 'center'
					}}>
					<GridItem
						className={css({zIndex: 10})}
						order={{base: 1, smDown: 2}}
						width="100%"
						lg={{minW: 496}}>
						{props.content && (
							<ContentBlock {...props.content}>
								<FormProvider {...methods}>
									<form
										onSubmit={(e) => {
											// Mouse is explicitly set when clicked. null || 'keyboard' is from enter submission.
											emitVerifyCodeEvent(e, {
												status: 'initiated',
												submitSource:
													submitSource.current === 'mouse'
														? 'mouse'
														: 'keyboard'
											});
											submitSource.current = null; // reset for next submit
											methods
												.handleSubmit(onValidSubmit)(e)
												.catch((err) => {
													console.error('Failed to submit', err);
												});
										}}>
										<Stack gap={24}>
											<FormTextField
												className="nested-link"
												required
												name="code"
												error={
													errorMessage &&
													typeof errorMessage === 'string' && (
														<SafeHTML
															dangerouslySetInnerHTML={{
																__html: errorMessage
															}}
														/>
													)
												}
												disabled={!!cmsRedeemDisabled}
												label={messages['epic.app.redeem.enterPin']}
												title={messages['epic.app.required.field.text']}
												mask={codeMask}
												size="lg"
												onInput={() => reset()}
												inputProps={{
													ref: inputRef,
													style: {textTransform: 'uppercase'}
												}}
												endAdornment={
													<WithClickOpen>
														{({open, setOpen, innerRef}) => {
															const handleOpen = () => setOpen(true);
															const handleClose = () =>
																setOpen(false);

															return (
																<Tooltip
																	open={open}
																	className={tooltipStyles}
																	side="bottom"
																	label={
																		<Stack
																			gap={16}
																			align="center">
																			<Text variant="paragraphXS">
																				{
																					messages[
																						'epic.app.redeem.tooltip'
																					]
																				}
																			</Text>
																			<img
																				width={150}
																				src={getStaticAssetPath(
																					'Fortnite_Gift_Card_Tooltip.png'
																				)}
																				alt="Gift Card Tooltip"
																			/>
																		</Stack>
																	}>
																	<Button
																		ref={innerRef}
																		variant="ghost"
																		size="sm"
																		icon={
																			<QuestionCircleIcon
																				style={{
																					color: 'white'
																				}}
																			/>
																		}
																		onClick={handleOpen}
																		onMouseEnter={handleOpen}
																		onMouseLeave={handleClose}
																		onFocus={handleOpen}
																		onBlur={handleClose}
																	/>
																</Tooltip>
															);
														}}
													</WithClickOpen>
												}
											/>
											<Box>
												<Button
													size="xl"
													className={buttonStyles}
													type="submit"
													disabled={disableSubmit}
													onMouseDown={() => {
														submitSource.current = 'mouse';
													}}
													// Don't replace this with getOnKeyDownProp - e.preventDefault stops form submission.
													onKeyDown={(e) => {
														if (KEYBOARD_SUBMIT_KEYS.includes(e.key))
															submitSource.current = 'keyboard';
													}}
													loading={
														mounted && (isLoading || completedStep)
													}
													loadingLabel={
														messages['epic.app.redeem.card.cta']
													}>
													{messages['epic.app.redeem.card.cta']}
												</Button>
											</Box>
										</Stack>
									</form>
								</FormProvider>
							</ContentBlock>
						)}
					</GridItem>

					<GridItem order={{base: 2, smDown: 1}}>
						{props.image?.image && (
							<Asset
								unoptimized
								asset={props.image?.image}
								width={isTablet ? 412 : 597}
								height={isMobile ? 251 : 604}
								className={css({
									objectFit: 'contain',
									aspectRatio: '597/604',
									smDown: {
										width: 'full',
										height: 'auto',
										aspectRatio: '412/251'
									}
								})}
							/>
						)}
					</GridItem>
				</Grid>
			</Container>
		</>
	);
};
